import { LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import type { WorkCredit } from '@/components/WorkDetailCard'
import { safeCustomElement } from '@/utilities/custom-element'
import type { WorkDetail } from './WorkGroup'

const jsonConverter = {
  fromAttribute: (value: string | null): WorkCredit[] => {
    if (!value) return []
    try {
      return JSON.parse(value)
    } catch {
      return []
    }
  },
  toAttribute: () => null,
}

/**
 * Data-carrier element for declarative composition inside <mdc-work-group>.
 * Does not render anything — WorkGroup reads these children to build its works list.
 *
 * Usage:
 * ```html
 * <mdc-work-group size="lg">
 *   <mdc-work-thumb
 *     src="https://..."
 *     alt="Campaign title"
 *     href="/work/campaign-slug"
 *     card-image-src="https://..."
 *     card-title="Client Name"
 *     campaign-type="Editorial"
 *     campaign-name="Spring 2025"
 *     picture-credits='[{"role":"Model","name":"Jane Doe","href":"/people/jane-doe"}]'
 *     work-credits='[{"role":"Photographer","name":"Mario Testino"}]'
 *   ></mdc-work-thumb>
 * </mdc-work-group>
 * ```
 */
@safeCustomElement('mdc-work-thumb')
export class MdcWorkThumb extends LitElement {
  @property() src = ''
  @property() alt = ''
  @property() href = ''
  @property({ attribute: 'card-image-src' }) cardImageSrc = ''
  @property({ attribute: 'card-title' }) cardTitle = ''
  @property({ attribute: 'campaign-type' }) campaignType = ''
  @property({ attribute: 'campaign-name' }) campaignName = ''
  @property({ converter: jsonConverter, attribute: 'picture-credits' })
  pictureCredits: WorkCredit[] = []
  @property({ converter: jsonConverter, attribute: 'work-credits' })
  workCredits: WorkCredit[] = []

  /** Convert this element's attributes to a WorkDetail object. */
  toWorkDetail(): WorkDetail {
    return {
      src: this.src,
      alt: this.alt,
      href: this.href,
      cardImageSrc: this.cardImageSrc || undefined,
      cardTitle: this.cardTitle || undefined,
      campaignType: this.campaignType || undefined,
      campaignName: this.campaignName || undefined,
      pictureCredits: this.pictureCredits.length ? this.pictureCredits : undefined,
      workCredits: this.workCredits.length ? this.workCredits : undefined,
    }
  }

  // Renders nothing — this is a data-only element
  override createRenderRoot() {
    return this
  }

  override render() {
    return undefined
  }
}
